import { defineComponent, reactive, onMounted, watch } from '@vue/composition-api';
import axios from 'axios';
import { SettingsContext } from '@/utilities/settings-context';
import { Orders, Invoices, ListArgs } from './billing-types';
import { formatDate, formatAmount, formatStringDateOnly, formatStringDate } from '@/functions/format-functions';
import { calcVat } from '@/functions/calc-functions';
import { alertDialogue, generalError } from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';
import { createPageContext, PageAction } from "@/components/navbar-box/page-context";
import { BaseOrder, InvoiceStatus, IsOrder } from '../admin-billing/admin-billing-types';
import { getOrderPeriod } from '@/functions/billing';
import { CreditInformation, CreditTransactionItem, getCreditInformation, getCreditTransactions } from '@/functions/credit-functions';
import CompanyOrders from '@/components/company-orders/company-orders.vue';
import CompanyInvoices from '@/components/company-invoices/company-invoices.vue';
import CompanyCreditTransactions from '@/components/company-credit-transactions/company-credit-transactions.vue';
import CompanyCreditBalance from '@/components/company-credit-balance/company-credit-balance.vue';
import { sortPromotionType } from '@/states/promotion-state';

type SelectedTab = 'billing' | 'credit';

export default defineComponent({
  components: {
    CompanyOrders,
    CompanyInvoices,
    CompanyCreditTransactions,
    CompanyCreditBalance,
  },
  setup(_, context) {

    const settings = reactive({
      companyId: 0,
      ordersCurrent: 1,
      invoicesCurrent: 1,
      perPage: 4,
      data: {
        orders: null as Orders | null,
        invoices: null as Invoices | null,
        creditInformation: {
          enabled: false,
          maxPeriodDays: 0,
          updatedAt: new Date(),
          amounts: [],
        } as CreditInformation,
        creditTransactions: null as CreditTransactionItem[] | null,
      },
      creditTransactionsLoading: false,
      creditTransactionsLoaded: false,
      loaded: false,
      currentInvoiceId: null as unknown as number,
      currentOrders: null as unknown as Array<BaseOrder>,
      selectedTab: 'billing' as SelectedTab,
    });

    const getOrders = async (args: Partial<ListArgs>) => {

      if (!args.id) {

        args.id = settings.companyId;
      }

      const response = await axios.get(SettingsContext.getApiUrl('getOrders'), { params: { ...args } });

      settings.data.orders = response.data;
    }

    const getInvoices = async (args: Partial<ListArgs>) => {

      if (!args.id) {

        args.id = settings.companyId;
      }

      const response = await axios.get<Invoices>(SettingsContext.getApiUrl('getInvoices'), { params: { ...args } });

      settings.data.invoices = {
        total: response.data.total,
        data: response.data.data.sort((a, b) => b.id - a.id),
      };
    }


    watch(() => context.root.$route.query, (query) => {
      if (query.tab) {
        settings.selectedTab = query.tab as SelectedTab;
      }
      else {
        settings.selectedTab = 'billing';
      }
    });

    const selectTab = async (tab: SelectedTab) => {
      if (settings.selectedTab !== tab) {
        context.root.$router.push({ query: { tab } }).catch(() => {
          // ignore
        });
        settings.selectedTab = tab;
      }

      if (tab === 'credit' && !settings.creditTransactionsLoaded) {
        const creditInformation = await getCreditInformation(settings.companyId);

        settings.data.creditInformation = {
          ...creditInformation,
          amounts: creditInformation.amounts.filter(f => f.type !== 'volume').sort((a, b) => sortPromotionType(a.type, b.type)),
        };

        settings.creditTransactionsLoading = true;

        settings.data.creditTransactions = await getCreditTransactions(settings.companyId, 1, 1000);
        settings.creditTransactionsLoading = false;
        settings.creditTransactionsLoaded = true;
      }
      else if (!settings.loaded) {

        const orders = getOrders({ id: settings.companyId });

        const invoices = getInvoices({ id: settings.companyId });

        await Promise.all([orders, invoices]);

        settings.loaded = true;
      }
    };

    onMounted(async () => {

      if (context.root.$route.params.company) {

        settings.companyId = parseInt(context.root.$route.params.company) as number;

        const tab = (context.root.$route.query?.tab ?? 'billing') as SelectedTab;

        const creditInformation = await getCreditInformation(settings.companyId);

        settings.data.creditInformation = creditInformation;

        await selectTab(tab);
      }
    });

    const requestForInvoice = async (id: number) => {

      try {

        await axios.post(SettingsContext.getApiUrl('RequestForInvoice'), { id });

        await getOrders({ id: settings.companyId });

        await alertDialogue(i18n.t('pages.admin-billing.dialogue.request-invoice') as string, i18n.t('pages.admin-billing.dialogue.requested-invoice') as string);

      } catch {

        await generalError(false);
      }
    }

    const pageContext = createPageContext({
      actions: [{ name: `<i class="mdi mdi-cash-register"></i> ${i18n.t('general.request-invoice')}`, action: () => requestForInvoice(settings.companyId), enabled: false }],
      title: { text: '' },
      breadcrumb: {
        showHome: false,
        segments: [
          { text: i18n.t('home.breadcrumb-settings') as string, route: `/${context.root.$route.params.company}/settings`, icon: '<i class="mdi mdi-office-home-cog"></i>' },
          { text: i18n.t('pages.settings.billing') as string, icon: '<i class="mdi mdi-cash-register"></i>' }
        ]
      }
    });

    watch(() => settings?.data?.orders?.orders?.length, () => (pageContext.actions[0] as PageAction).enabled = settings?.data?.orders?.orders?.length != null && settings?.data?.orders?.orders?.length > 0);

    return {

      settings,

      calcVat,

      formatDate,

      formatAmount,

      formatStringDate,

      formatStringDateOnly,

      getInvoices,

      getOrders,

      pageContext,

      InvoiceStatus,

      getOrderPeriod,

      IsOrder,

      selectTab,

      requestForInvoice: () => requestForInvoice(settings.companyId),
    }
  }
})